import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useRoutes } from '@localstack/services';
import { CombinedUserInfo, DocumentType, PlannedUsageType, SignupSurvey } from '@localstack/types';
import pickBy from 'lodash/pickBy';
import { useLocation } from 'react-router-dom';

import { getPreSignInPageURL, updatePreSignInPageURL } from '~/util/storage';
import { AppRoute } from '~/config';

import { AuthQueryParamKeys, PlanParamValues } from './types';

const mergeSearchParams = (a: URLSearchParams, b: URLSearchParams) => {
  const merged = new URLSearchParams(a);
  b.forEach((value, key) => {
    if (!merged.has(key)) {
      merged.set(key, value);
    }
  });
  return merged;
};

type PasswordFieldVisibilityReturnType = {
  showPasswordField: boolean;
  setShowPasswordField: React.Dispatch<React.SetStateAction<boolean>>;
};

export const usePasswordFieldVisibility = (
  emailFieldErrors: Optional<FieldError>,
  passwordFieldRef: React.RefObject<HTMLInputElement>,
): PasswordFieldVisibilityReturnType => {
  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);

  useEffect(() => {
    if (emailFieldErrors) {
      setShowPasswordField(false);
    }
  }, [emailFieldErrors]);

  useEffect(() => {
    if (showPasswordField) {
      passwordFieldRef.current?.focus();
    }
  }, [showPasswordField]);

  return {
    showPasswordField,
    setShowPasswordField,
  };
};

type UsePreSignInUrlReturnType = {
  navigateToPreSignInUrl: () => void;
  isPreSignUrlPresent: boolean;
};

export const usePreSignInUrl = (): UsePreSignInUrlReturnType => {
  const { goto } = useRoutes();
  const preSignInUrl = getPreSignInPageURL();

  const isPreSignUrlPresent = !!preSignInUrl;

  const navigateToPreSignInUrl = useCallback(() => {
    if (preSignInUrl) {
      updatePreSignInPageURL(null);
      goto(preSignInUrl);
    }
  }, [preSignInUrl]);

  return {
    navigateToPreSignInUrl,
    isPreSignUrlPresent,
  };
};

export const useLegalDocument = (): DocumentType => {
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const isFreemiumPlanSignup = queryParams.get(AuthQueryParamKeys.PLAN) === PlanParamValues.FREE;
  return isFreemiumPlanSignup ? DocumentType.TOS : DocumentType.MSA;
};

const getPlanFromSignupSurvey = (signupSurvey: SignupSurvey): PlanParamValues | undefined => {
  if (!signupSurvey.planned_usage_type) return;

  const mapping: Partial<Record<PlannedUsageType, PlanParamValues>> = {
    [PlannedUsageType.MARKETPLACE]: PlanParamValues.MARKETPLACE,
    [PlannedUsageType.FREEMIUM]: PlanParamValues.FREE,
  };
  return mapping[signupSurvey.planned_usage_type];
};

const buildQueryParamsFromSignupSurvey = (signupSurvey: SignupSurvey): URLSearchParams => {
  const params = {
    [AuthQueryParamKeys.PLAN]: getPlanFromSignupSurvey(signupSurvey),
    [AuthQueryParamKeys.PROMO]: signupSurvey.promo_code,
    [AuthQueryParamKeys.EMULATOR]: signupSurvey.emulator,
  };
  const filteredParams = pickBy(params, (value) => value !== undefined && value !== null);

  return new URLSearchParams(filteredParams);
};

type UsePostAuthRedirectReturnType = {
  redirect: (freshUserInfo: CombinedUserInfo) => void;
};

export const usePostAuthRedirect = (): UsePostAuthRedirectReturnType => {
  const { goto } = useRoutes();
  const { navigateToPreSignInUrl, isPreSignUrlPresent } = usePreSignInUrl();
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const isStudentPlanSignup = queryParams.get(AuthQueryParamKeys.PLAN) === PlanParamValues.STUDENT;

  const redirect = useCallback(
    (freshUserInfo: CombinedUserInfo) => {
      const signupSurvey = freshUserInfo.user.signup_survey;

      if (signupSurvey?.require_finish_account_creation) {
        goto(
          AppRoute.ONBOARDING,
          {},
          mergeSearchParams(queryParams, buildQueryParamsFromSignupSurvey(signupSurvey)).toString(),
        );
        return;
      }
      if (isPreSignUrlPresent) {
        navigateToPreSignInUrl();
        return;
      }
      if (signupSurvey?.is_student || isStudentPlanSignup) {
        goto(AppRoute.STUDENT_VERIFICATION);
        return;
      }
      goto(AppRoute.GETTING_STARTED);
    },
    [goto, navigateToPreSignInUrl, isPreSignUrlPresent, isStudentPlanSignup, queryParams],
  );

  return { redirect };
};

type UseRedirectIfAuthenticatedProps = {
  isPublicRoute?: boolean;
  isGuestOnlyRoute?: boolean;
  userInfo?: Optional<CombinedUserInfo>;
};

export const useAuthenticatedRouteGuard = ({
  isPublicRoute,
  isGuestOnlyRoute,
  userInfo,
}: UseRedirectIfAuthenticatedProps = {}): void => {
  const { pathname: activePathName } = useLocation();
  const isAuthenticated = userInfo?.roles.includes('authenticated');
  const { redirect } = usePostAuthRedirect();
  const signupSurvey = userInfo?.user.signup_survey;
  const needsAccountCompletion = signupSurvey?.require_finish_account_creation;
  // Snapshot of auth state when the user first landed on this page.
  // Used to distinguish "arrived already signed in" from "just signed in via form".
  const authenticatedOnArrival = useRef(isAuthenticated);

  useEffect(() => {
    if (!authenticatedOnArrival.current) return;
    if (!isAuthenticated || !userInfo) return;

    const isAuthOptionalRoute = isPublicRoute && !isGuestOnlyRoute;
    if (isAuthOptionalRoute) return;

    const isOnboardingRouteWithCompleteAccount = activePathName === AppRoute.ONBOARDING && !needsAccountCompletion;
    if (needsAccountCompletion || isPublicRoute || isOnboardingRouteWithCompleteAccount) {
      redirect(userInfo);
    }
  }, [isPublicRoute, isAuthenticated, signupSurvey]);
};
